import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { X, ShoppingBag, Star, Share2, Shield, Leaf, Zap, Clock, Crown, MessageSquare, Check } from 'lucide-react';
import { BASE_URL, ENDPOINTS, getImageUrl, addToCart, getProductReviews, deleteReview } from '../../apiConfig';
import './QuickViewModal.css';

const isSameVariant = (v1, v2) => {
  if (!v1 || !v2) return false;
  const id1 = v1._id || v1.id;
  const id2 = v2._id || v2.id;
  if (id1 && id2) return id1 === id2;
  const n1 = v1.size || v1.volume || v1.name;
  const n2 = v2.size || v2.volume || v2.name;
  if (n1 && n2) return n1 === n2;
  return v1 === v2;
};

const QuickViewModal = ({ product, isOpen, onClose }) => {
  const [liveProduct, setLiveProduct] = useState(product);
  const [activeImg, setActiveImg] = useState(product?.img);
  const [selectedVol, setSelectedVol] = useState(product?.volume || '100ml');
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.find(v => v.isDefault) || product?.variants?.[0] || null);
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const imgRef = useRef(null);
  const contentRef = useRef(null);

  // Sync state when product changes
  useEffect(() => {
    if (product) {
      const pCopy = JSON.parse(JSON.stringify(product));
      setLiveProduct(pCopy);
      setActiveImg(product.images?.[0] || product.img);
      setSelectedVol(pCopy.volume || '100ml');
      setSelectedVariant(pCopy.variants?.find(v => v.isDefault) || pCopy.variants?.[0] || null);
      
      const fetchReviews = async () => {
        setLoadingReviews(true);
        const fetched = await getProductReviews(product._id || product.id);
        setReviews(fetched);
        setLoadingReviews(false);
      };
      fetchReviews();
    }
  }, [product]);

  // Silent background polling for real-time stock updates
  useEffect(() => {
    if (!isOpen || !product) return;
    const productId = product._id || product.id;
    if (!productId) return;

    const pollStock = async () => {
      try {
        const res = await fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}?t=${Date.now()}`, { cache: 'no-store' });
        const result = await res.json();
        const prodData = Array.isArray(result) ? result : (result?.data || []);
        const freshOriginal = prodData.find(p => (p._id || p.id) === productId);
        const fresh = freshOriginal ? JSON.parse(JSON.stringify(freshOriginal)) : null;
        if (!fresh) return;

        // Update live product stock silently
        setLiveProduct(prev => {
          if (!prev) return fresh;
          const updated = { ...fresh };
          // Preserve locally deducted stock by using Math.min with the fresh stock
          if (prev.variants && updated.variants) {
            updated.variants = updated.variants.map(uv => {
              const pv = prev.variants.find(v => isSameVariant(v, uv));
              if (pv) {
                const pStock = pv.stock !== undefined ? pv.stock : pv.quantity;
                const uStock = uv.stock !== undefined ? uv.stock : uv.quantity;
                const finalStock = Math.min(Number(pStock || 0), Number(uStock || 0));
                return { ...uv, stock: finalStock, quantity: finalStock };
              }
              return uv;
            });
          } else {
            const pStock = prev.stock !== undefined ? prev.stock : prev.quantity;
            const uStock = updated.stock !== undefined ? updated.stock : updated.quantity;
            const finalStock = Math.min(Number(pStock || 0), Number(uStock || 0));
            updated.stock = finalStock;
            updated.quantity = finalStock;
          }
          return updated;
        });

        // Update selectedVariant stock without changing user's selection
        setSelectedVariant(prev => {
          if (!prev) return prev;
          const updatedVariant = fresh.variants?.find(v => isSameVariant(v, prev));
          if (updatedVariant) {
            const prevStock = prev.stock !== undefined ? prev.stock : prev.quantity;
            const newStock = updatedVariant.stock !== undefined ? updatedVariant.stock : updatedVariant.quantity;
            // Use Math.min so local deducted stock is never overwritten by stale backend value
            const finalStock = Math.min(Number(prevStock ?? 9999), Number(newStock ?? 9999));
            return { ...prev, stock: finalStock, quantity: finalStock, inStock: updatedVariant.inStock };
          }
          return prev;
        });
      } catch (err) {
        // Silent fail - don't disrupt user experience
      }
    };

    const pollInterval = setInterval(pollStock, 4000);
    return () => clearInterval(pollInterval);
  }, [isOpen, product]);

  // eslint-disable-next-line no-unused-vars
  const handleDeleteReview = async (reviewId) => {
    try {
      await deleteReview(reviewId);
      setReviews(reviews.filter(r => (r._id || r.id) !== reviewId));
    } catch (err) {
      console.error("Failed to delete review:", err);
      alert(err.message || "Failed to delete review");
    }
  };

  // Animation when image changes
  useEffect(() => {
    if (activeImg && imgRef.current) {
      gsap.fromTo(imgRef.current,
        { opacity: 0, scale: 0.95 },
        { opacity: 1, scale: 1, duration: 0.5, ease: 'power2.out' }
      );
    }
  }, [activeImg]);

  const currentStock = (() => {
    if (selectedVariant) {
      if (selectedVariant.stock !== undefined) return Number(selectedVariant.stock);
      if (selectedVariant.quantity !== undefined) return Number(selectedVariant.quantity);
      if (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false') return 0;
    }
    if (liveProduct?.stock !== undefined) return Number(liveProduct.stock);
    if (liveProduct?.quantity !== undefined) return Number(liveProduct.quantity);
    if (liveProduct?.variants && liveProduct.variants.length > 0) {
      const variant = liveProduct.variants[0];
      if (variant.stock !== undefined) return Number(variant.stock);
      if (variant.quantity !== undefined) return Number(variant.quantity);
    }
    return null;
  })();

  // Cap quantity if it exceeds stock when switching variants
  useEffect(() => {
    if (currentStock !== null && quantity > currentStock) {
      setQuantity(Math.max(1, currentStock));
    }
  }, [selectedVariant, currentStock, quantity]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const tl = gsap.timeline();

      tl.to(overlayRef.current, { opacity: 1, duration: 0.4, ease: 'power2.out' })
        .to(modalRef.current, { display: 'flex', opacity: 1, y: 0, duration: 0.6, ease: 'expo.out' }, "-=0.2");

      // Simple reveal for all content instead of staggered hidden state
      gsap.fromTo(".qv-animate",
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.05, ease: 'power2.out' }
      );

      gsap.fromTo(imgRef.current,
        { opacity: 0, scale: 0.9 },
        { opacity: 1, scale: 1, duration: 0.8, ease: 'expo.out' }
      );
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleClose = () => {
    const tl = gsap.timeline({ onComplete: onClose });
    tl.to(modalRef.current, { y: 50, opacity: 0, duration: 0.4, ease: 'power2.in' })
      .to(overlayRef.current, { opacity: 0, duration: 0.3 }, "-=0.2");
  };

  // Compute average rating from actual customer reviews
  const avgRating = (() => {
    if (!reviews || reviews.length === 0) return null;
    const total = reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0);
    return total / reviews.length;
  })();

  if (!isOpen) return null;

  // Use product images if available, otherwise fallback to main image array
  const displayImages = (product.images && product.images.length > 0) ? product.images : [product.img, product.img, product.img, product.img];

  // eslint-disable-next-line no-unused-vars
  const getProductStock = () => currentStock;



  const isOutOfStock = 
    currentStock === 0 || 
    (selectedVariant && (selectedVariant.inStock === false || String(selectedVariant.inStock) === 'false')) ||
    liveProduct?.inStock === false || 
    liveProduct?.isAvailable === false ||
    liveProduct?.status === 'OutOfStock' ||
    liveProduct?.stockStatus === 'OutOfStock';

  const renderStockStatus = () => {
    const stock = currentStock;

    if (isOutOfStock) {
      return (
        <span className="qv-stock-badge qv-stock-out">
          <span className="qv-stock-dot" />
          <span>OUT OF STOCK</span>
        </span>
      );
    }

    if (stock !== null) {
      if (stock <= 5) {
        return (
          <span className="qv-stock-badge qv-stock-low">
            <span className="qv-stock-dot" />
            <span>ONLY {stock} LEFT</span>
          </span>
        );
      }
      return (
        <span className="qv-stock-badge qv-stock-in">
          <span className="qv-stock-dot" />
          <span>{stock} IN STOCK</span>
        </span>
      );
    }

    return (
      <span className="qv-stock-badge qv-stock-in">
        <span className="qv-stock-dot" />
        <span>IN STOCK</span>
      </span>
    );
  };

  return (
    <div className="qv-modern-overlay" ref={overlayRef} onClick={handleClose}>
      <div className="qv-modern-modal" ref={modalRef} onClick={e => e.stopPropagation()}>
        {showToast && (
          <div className="qv-toast-popup">
            <div className="qv-toast-content">
              <Check size={16} className="qv-toast-icon" />
              <span>ADDED TO CART</span>
            </div>
          </div>
        )}
        <button className="qv-modern-close" onClick={handleClose}>
          <X size={20} />
        </button>

        <div className="qv-modern-grid">
          {/* Left: Product Visual */}
          <div className="qv-visual-pane">
            <div className="qv-visual-bg" />

            <div className="qv-visual-anchor">
              <div className="qv-visual-circle">
                <img src={getImageUrl(activeImg)} alt={product.name} className="qv-main-img" ref={imgRef} />
              </div>
            </div>

            {/* Vertical Thumbnails */}
            <div className="qv-visual-thumbs qv-animate">
              {displayImages.map((img, idx) => (
                <div
                  key={idx}
                  className={`qv-v-thumb ${activeImg === img ? 'active' : ''}`}
                  onClick={() => setActiveImg(img)}
                >
                  <img src={getImageUrl(img)} alt={`thumb-${idx}`} />
                </div>
              ))}
            </div>

            <div className="qv-visual-tag qv-animate">
              <Crown size={14} />
              <span>PREMIUM COLLECTION</span>
            </div>

            {/* Bottom Stats Bar */}
            <div className="qv-visual-stats qv-animate">
              <div className="qv-v-stat">
                <Clock size={16} />
                <div className="qv-v-stat-text"><span>{product.stayingPower || '12H+'}</span> Long Lasting</div>
              </div>
              <div className="qv-v-divider" />
              <div className="qv-v-stat">
                <Leaf size={16} />
                <div className="qv-v-stat-text"><span>{product.origin || 'Organic'}</span> Natural Base</div>
              </div>
              <div className="qv-v-divider" />
              <div className="qv-v-stat">
                <Zap size={16} />
                <div className="qv-v-stat-text"><span>{product.projection || 'Fast'}</span> Projection</div>
              </div>
            </div>
          </div>

          {/* Right: Product Details */}
          <div className="qv-details-pane" ref={contentRef}>
            <div className="qv-brand-header qv-animate">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className="qv-category">{product.collection || 'Exclusive'}</span>
                {renderStockStatus()}
              </div>
              <div className="qv-rating">
                {avgRating !== null ? (
                  <>
                    <div className="qv-stars-wrap">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={12}
                          fill={i < Math.floor(avgRating) ? "#FFB800" : "rgba(0, 0, 0, 0.15)"}
                          color={i < Math.floor(avgRating) ? "#FFB800" : "rgba(0, 0, 0, 0.15)"}
                        />
                      ))}
                    </div>
                    <span className="qv-rating-num">
                      {avgRating.toFixed(1)} <small>/ 5.0</small>
                    </span>
                  </>
                ) : (
                  <span className="qv-rating-num" style={{ opacity: 0.45, fontSize: '0.7rem' }}>No Reviews Yet</span>
                )}
              </div>
            </div>

            <h2 className="qv-main-title qv-animate">{product.name}</h2>
            <div className="qv-tagline qv-animate">A SCENT OF POWER. A SIGNATURE OF LUXURY.</div>
            <p className="qv-main-desc qv-animate">
              {product.description || 'A masterful blend of rare ingredients, meticulously crafted to leave a lasting impression of sophistication and power.'}
            </p>

            <div className="qv-feature-grid qv-animate">
              {(product.features || ['Long Lasting', 'Organic Base', 'Fast Projection']).map((feat, idx) => (
                <div key={idx} className="qv-feat">
                  {idx === 0 && <Shield size={18} />}
                  {idx === 1 && <Leaf size={18} />}
                  {idx === 2 && <Zap size={18} />}
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            <div className="qv-config qv-animate">
              <div className="qv-config-label">
                <span>Select Volume</span>
                <span>{selectedVariant ? (selectedVariant.size || selectedVariant.volume || selectedVariant.name) : selectedVol}</span>
              </div>
              <div className="qv-vol-selector">
                {liveProduct?.variants && liveProduct.variants.length > 0 ? (
                  [...liveProduct.variants].sort((a, b) => {
                    const getVol = (v) => {
                      const str = String(v.size || v.volume || v.name || "");
                      const match = str.match(/(\d+)/);
                      return match ? parseInt(match[1], 10) : 0;
                    };
                    return getVol(a) - getVol(b);
                  }).map((v, idx) => {
                    const variantName = v.size || v.volume || v.name || `Variant ${idx + 1}`;
                    const isActive = selectedVariant
                      ? isSameVariant(v, selectedVariant)
                      : idx === 0;
                    const vStock = v.stock !== undefined ? Number(v.stock) : (v.quantity !== undefined ? Number(v.quantity) : null);
                    const vOos = vStock === 0 || v.inStock === false || String(v.inStock) === 'false';
                    return (
                      <button
                        key={v._id || v.id || idx}
                        className={`qv-vol-item ${isActive ? 'active' : ''} ${vOos ? 'oos' : ''}`}
                        onClick={() => !vOos && setSelectedVariant(v)}
                        title={vOos ? 'Out of Stock' : `${vStock ?? ''} in stock`}
                      >
                        <span className="qv-vol-name">{variantName}</span>
                        {vStock !== null && (
                          <span className="qv-vol-stock">{vOos ? 'Sold Out' : `${vStock} left`}</span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  ['50ml', '100ml', '200ml'].map(v => (
                    <button
                      key={v}
                      className={`qv-vol-item ${selectedVol.toLowerCase() === v.toLowerCase() ? 'active' : ''}`}
                      onClick={() => setSelectedVol(v)}
                    >
                      {v}
                    </button>
                  ))
                )}
              </div>
            </div>

            <div className="qv-purchase-pane qv-animate">
              <div className="qv-qty-selector">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={isOutOfStock || quantity <= 1}
                >
                  -
                </button>
                <span>{isOutOfStock ? 0 : quantity}</span>
                <button 
                  onClick={() => setQuantity(Math.min(currentStock || 1, quantity + 1))}
                  disabled={isOutOfStock || (currentStock !== null && quantity >= currentStock)}
                >
                  +
                </button>
              </div>

              <div className="qv-price-info">
                <span className="qv-p-label">PRICE</span>
                <span className="qv-p-val">
                  {(() => {
                    if (liveProduct?.variants && liveProduct.variants.length > 0 && selectedVariant) {
                      const productPrice = selectedVariant.finalPrice || selectedVariant.price || 0;
                      const finalPrice = Math.round(productPrice * (isOutOfStock ? 0 : quantity));
                      return `RS. ${finalPrice.toLocaleString()}`;
                    } else {
                      const variantInfo = liveProduct?.variants?.find(v => v.isDefault) || liveProduct?.variants?.[0] || {};
                      const productPrice = variantInfo.finalPrice || variantInfo.price || liveProduct?.price || 0;
                      const basePrice = parseInt(String(productPrice || 0).replace(/[^0-9]/g, '')) || 0;
                      let multiplier = 1;
                      if (selectedVol.toLowerCase() === '50ml') multiplier = 0.6;
                      if (selectedVol.toLowerCase() === '200ml') multiplier = 1.8;
                      const finalPrice = Math.round(basePrice * multiplier * (isOutOfStock ? 0 : quantity));
                      return `RS. ${finalPrice.toLocaleString()}`;
                    }
                  })()}
                </span>
              </div>
              <button
                className={`qv-add-btn ${showToast ? 'added' : ''} ${isOutOfStock ? 'qv-btn-out' : ''}`}
                disabled={isAdding}
                onClick={() => {
                  if (isOutOfStock) {
                    window.dispatchEvent(new CustomEvent('productOutOfStock', { 
                      detail: { productName: product.name } 
                    }));
                    return;
                  }
                  const id = product.id || product._id;
                  // Snapshot the selected variant at click time
                  const activeVar = selectedVariant
                    ? (liveProduct?.variants?.find(v => isSameVariant(v, selectedVariant)) || selectedVariant)
                    : (liveProduct?.variants?.[0] || null);
                  const variantId = activeVar ? (activeVar._id || activeVar.id) : id;
                  const volumeToSend = activeVar ? (activeVar.size || activeVar.volume || activeVar.name) : selectedVol;
                  if (id) {
                    setIsAdding(true);
                    addToCart(id, variantId, quantity, volumeToSend)
                      .then(() => {
                        setShowToast(true);
                        setIsAdding(false);
                        setTimeout(() => {
                          setShowToast(false);
                        }, 4000);

                        // Deduct stock ONLY for the exact variant user bought
                        // currentStock is already computed reliably from selectedVariant or liveProduct
                        const stockNow = (() => {
                          if (activeVar) {
                            if (activeVar.stock !== undefined) return Number(activeVar.stock);
                            if (activeVar.quantity !== undefined) return Number(activeVar.quantity);
                          }
                          return currentStock !== null ? currentStock : 0;
                        })();
                        const remaining = Math.max(0, stockNow - quantity);

                        if (activeVar) {
                          const updatedVar = { ...activeVar, stock: remaining, quantity: remaining };
                          setSelectedVariant(updatedVar);
                          if (liveProduct?.variants) {
                            setLiveProduct(prev => {
                              if (!prev || !prev.variants) return prev;
                              const newVariants = prev.variants.map(v =>
                                isSameVariant(v, activeVar)
                                  ? { ...v, stock: remaining, quantity: remaining }
                                  : v
                              );
                              return { ...prev, variants: newVariants };
                            });
                          }
                        } else {
                          setLiveProduct(prev => ({ ...prev, stock: remaining, quantity: remaining }));
                        }
                        setQuantity(1);
                      })
                      .catch(err => {
                        console.error(err);
                        setIsAdding(false);
                      });
                  }
                }}
              >
                {isOutOfStock ? (
                  <>
                    <X size={20} />
                    <span>OUT OF STOCK</span>
                  </>
                ) : showToast ? (
                  <>
                    <Check size={20} />
                    <span>ADDED</span>
                  </>
                ) : (
                  <>
                    <ShoppingBag size={20} />
                    <span>{isAdding ? 'ADDING...' : 'ADD TO CART'}</span>
                  </>
                )}
              </button>
            </div>

            <div className="qv-footer-links qv-animate">
              <button><Share2 size={16} /> Share</button>
              <span>•</span>
              <button>View Details</button>
            </div>

            {/* Luxury Reviews Section */}
            <div className="qv-reviews-section qv-animate">
              <div className="qv-reviews-header">
                <div className="qv-reviews-title">
                  <MessageSquare size={16} />
                  <span>CUSTOMER ARCHIVE ({reviews.length})</span>
                </div>
              </div>

              <div className="qv-reviews-list">
                {loadingReviews ? (
                  <div className="qv-reviews-loading">Loading luxury reviews...</div>
                ) : reviews.length === 0 ? (
                  <div className="qv-reviews-empty">Be the first to review this masterpiece.</div>
                ) : (
                  reviews.map((rev) => (
                    <div key={rev._id || rev.id} className="qv-review-card">
                      <div className="qv-review-top">
                        <div className="qv-review-meta">
                          <span className="qv-review-author">{rev.userName || rev.user?.name || 'Anonymous'}</span>
                          <div className="qv-review-stars">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={10}
                                fill={i < (rev.rating || 5) ? "#FFB800" : "rgba(0, 0, 0, 0.15)"}
                                color={i < (rev.rating || 5) ? "#FFB800" : "rgba(0, 0, 0, 0.15)"}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="qv-review-comment">{rev.comment || rev.reviewText || 'A truly breathtaking olfactory masterpiece.'}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
